/**
 * The `Terms and Conditions` master, for the quotation's terms card.
 *
 * Two things are needed and they are different requests: the LIST, to choose from, and one
 * template's TEXT, fetched only when a purchaser actually picks it. Loading every template's body
 * to populate a dropdown would pull a dozen pages of HTML nobody asked to read.
 */

import { useQueryClient } from '@tanstack/react-query'
import { getResourceDoc } from './frappe'
import { str } from './wire'

/* The dropdown lives with the other master pickers (`useEnquiryFromMail.ts`), which owns the
   shared search/limit behaviour; re-exported here so the terms card has one import. */
export { useTermsPicker } from './useEnquiryFromMail'

export function useTermsTemplate() {
  const queryClient = useQueryClient()
  return {
    /**
     * One template's text, or `null` if it cannot be read.
     *
     * `null` rather than throwing: failing to fetch the body should still let the purchaser record
     * WHICH template they chose, and they can type or paste the text. Losing the selection as well
     * would make a network blip cost them both.
     */
    fetch: async (name: string): Promise<string | null> => {
      if (name.trim() === '') return null
      try {
        const doc = await queryClient.fetchQuery({
          queryKey: ['terms-template', name] as const,
          queryFn: () => getResourceDoc<Record<string, unknown>>('Terms and Conditions', name),
          staleTime: 30 * 60_000,
        })
        return str(doc.terms)
      } catch {
        return null
      }
    },
  }
}
